import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

import type { DocPresenceKey } from "@productlane/lib"
import { DOC_CURSOR_THROTTLE_MS } from "@productlane/lib"
import {
	USER_COLOR_PALETTE,
	UserColor,
} from "@productlane/lib/src/color-palette"

import type { RemoteCursor } from "@/lib/doc-presence-peers"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import type { EditorState } from "@tiptap/pm/state"
import { remoteCursors } from "@/lib/doc-presence-peers"
import { updateDocState } from "@/lib/sockets/doc-presence"
import { useDocPresenceState } from "@/lib/states/doc-presence-state"
import { pointAt, positionOf } from "./cursor-points"

interface RemoteCursorsOptions {
	getDocKey: () => DocPresenceKey | undefined
	getSelfUserId: () => string | undefined
	/** The seed avatars use for this teammate, so the caret matches their avatar. */
	getColorSeed: (userId: string) => string
}

const remoteCursorsKey = new PluginKey<DecorationSet>("remoteCursors")

// Same order and hash as the avatar fallback, so a caret matches its owner's avatar.
const CURSOR_COLORS = [
	UserColor.Purple,
	UserColor.Teal,
	UserColor.Green,
	UserColor.Yellow,
	UserColor.Orange,
	UserColor.Pink,
	UserColor.Red,
].map((color) => USER_COLOR_PALETTE[color].hex)

/** A stable colour per teammate for their caret and selection. */
export function cursorColor(seed: string): string {
	let hash = 0
	for (let i = 0; i < seed.length; i += 1) {
		hash = (hash << 5) - hash + seed.charCodeAt(i)
		hash |= 0
	}
	return CURSOR_COLORS[Math.abs(hash) % CURSOR_COLORS.length] ?? "#5e6ad2"
}

export function caret(name: string, color: string): HTMLElement {
	const caretEl = document.createElement("span")
	caretEl.className = "remote-caret"
	caretEl.style.borderColor = color
	const label = document.createElement("span")
	label.className = "remote-caret-label"
	label.style.backgroundColor = color
	label.textContent = name
	caretEl.appendChild(label)
	return caretEl
}

/** Carets and selections for everyone else in the doc, built from the shared peer list. */
export function buildCursorDecorations(
	doc: ProseMirrorNode,
	cursors: RemoteCursor[],
	getColorSeed: (userId: string) => string,
): DecorationSet {
	const decorations = cursors.flatMap(({ socketId, viewer, cursor }) => {
		const color = cursorColor(getColorSeed(viewer.userId))
		const anchor = positionOf(doc, cursor.anchor)
		const head = positionOf(doc, cursor.head)
		const from = Math.min(anchor, head)
		const to = Math.max(anchor, head)
		return [
			...(from < to
				? [
						Decoration.inline(from, to, {
							style: `background-color: ${color}33`,
						}),
					]
				: []),
			Decoration.widget(head, () => caret(viewer.name, color), {
				key: `${socketId}:${viewer.name}:${color}`,
				side: 1,
			}),
		]
	})
	return DecorationSet.create(doc, decorations)
}

/** What the carets look like, so a typing or join event that moves no caret redraws nothing. */
export function cursorsSignature(cursors: RemoteCursor[]): string {
	return cursors
		.map(
			({ socketId, viewer, cursor }) =>
				`${socketId}:${viewer.userId}:${viewer.name}:${JSON.stringify(cursor)}`,
		)
		.join("|")
}

/** Shares this reader's caret and draws teammates' carets, like Notion or Linear. */
export const RemoteCursors = Extension.create<RemoteCursorsOptions>({
	name: "remoteCursors",

	addOptions() {
		return {
			getDocKey: () => undefined,
			getSelfUserId: () => undefined,
			getColorSeed: (userId) => userId,
		}
	},

	addProseMirrorPlugins() {
		const { getDocKey, getSelfUserId, getColorSeed } = this.options
		let sentAt = 0
		let pending: ReturnType<typeof setTimeout> | null = null
		let sentKey: DocPresenceKey | undefined

		const send = (state: EditorState) => {
			const docKey = getDocKey()
			if (!docKey) return
			if (sentKey && sentKey !== docKey)
				updateDocState(sentKey, { cursor: null })
			sentKey = docKey
			sentAt = Date.now()
			const { anchor, head } = state.selection
			updateDocState(docKey, {
				cursor: {
					anchor: pointAt(state.doc, anchor),
					head: pointAt(state.doc, head),
				},
			})
		}

		const currentCursors = () => {
			const docKey = getDocKey()
			if (!docKey) return []
			const peers = useDocPresenceState.getState().docs[docKey] ?? {}
			return remoteCursors(peers, getSelfUserId())
		}

		const buildFor = (doc: ProseMirrorNode) =>
			buildCursorDecorations(doc, currentCursors(), getColorSeed)

		const clear = () => {
			if (pending) clearTimeout(pending)
			pending = null
			if (sentKey) updateDocState(sentKey, { cursor: null })
			sentKey = undefined
		}

		return [
			new Plugin<DecorationSet>({
				key: remoteCursorsKey,
				// Rebuilt from each teammate's last point, never mapped: their text lands
				// after their caret, so a mapped caret would jump to where the edit landed.
				state: {
					init: (_, state) => buildFor(state.doc),
					apply: (tr, set) =>
						tr.getMeta(remoteCursorsKey) || tr.docChanged
							? buildFor(tr.doc)
							: set,
				},
				props: {
					decorations: (state) => remoteCursorsKey.getState(state) ?? null,
					handleDOMEvents: {
						blur: () => {
							clear()
							return false
						},
					},
				},
				view: (view) => {
					let drawn = cursorsSignature(currentCursors())
					const unsubscribe = useDocPresenceState.subscribe((next, prev) => {
						const docKey = getDocKey()
						if (!docKey || next.docs[docKey] === prev.docs[docKey]) return
						if (view.isDestroyed) return
						const signature = cursorsSignature(currentCursors())
						if (signature === drawn) return
						drawn = signature
						const tr = view.state.tr
							.setMeta(remoteCursorsKey, true)
							.setMeta("addToHistory", false)
						// A caret redraw is not an edit: applied straight to the view it
						// skips the editor's transaction event and the React render it
						// triggers. Anything that would change the doc goes the normal way.
						const applied = view.state.apply(tr)
						if (applied.doc === view.state.doc) view.updateState(applied)
						else view.dispatch(tr)
					})

					return {
						update: (updated, prevState) => {
							if (!updated.hasFocus() || !updated.editable) return
							if (
								sentKey === getDocKey() &&
								prevState.selection.eq(updated.state.selection)
							) {
								return
							}
							if (pending) clearTimeout(pending)
							const wait = DOC_CURSOR_THROTTLE_MS - (Date.now() - sentAt)
							if (wait <= 0) {
								send(updated.state)
								return
							}
							pending = setTimeout(() => {
								pending = null
								send(updated.state)
							}, wait)
						},
						destroy: () => {
							unsubscribe()
							clear()
						},
					}
				},
			}),
		]
	},
})
